/**
 * UtilizationKpiCards
 *
 * 4 gradient KPI cards for the Utilization tab:
 * 1. Firm Utilization — overall utilization_pct as large XX%
 * 2. Median Utilization — reveals if adoption is concentrated or spread
 * 3. Active Attorneys — count with hourglass_hours > 0
 * 4. Hrs Captured — total hourglass hours reframed as value delivered
 */

import { UtilizationTotals, AttorneyUtilization } from "../../types/dashboard";
import { KPI_CARD_STYLES } from "../../config/dashboardTheme";

interface UtilizationKpiCardsProps {
  totals: UtilizationTotals;
  attorneys: AttorneyUtilization[];
}

interface CardProps {
  label: string;
  value: string;
  suffix?: string;
  gradient: string;
}

function computeMedianUtilization(attorneys: AttorneyUtilization[]): number {
  const pcts = attorneys
    .map((a) => a.utilization_pct)
    .sort((a, b) => a - b);
  if (pcts.length === 0) return 0;
  const mid = Math.floor(pcts.length / 2);
  return pcts.length % 2 === 0
    ? Math.round((pcts[mid - 1] + pcts[mid]) / 2)
    : pcts[mid];
}

function KpiCard({ label, value, suffix, gradient }: CardProps) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl border-0 shadow-lg bg-gradient-to-br ${gradient} p-4 min-w-[150px]`}
    >
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
      <p className="text-xs font-medium text-white/70 mb-1">{label}</p>
      <p className="text-2xl font-bold text-white">
        {value}
        {suffix && (
          <span className="text-sm font-normal text-white/70 ml-1">{suffix}</span>
        )}
      </p>
    </div>
  );
}

export default function UtilizationKpiCards({ totals, attorneys }: UtilizationKpiCardsProps) {
  const medianPct = computeMedianUtilization(attorneys);
  const activeCount = totals.attorney_count;

  return (
    <div className="flex flex-wrap gap-3">
      <KpiCard
        label="Mean Utilization"
        value={`${totals.utilization_pct}%`}
        gradient={KPI_CARD_STYLES.green}
      />
      <KpiCard
        label="Median Utilization"
        value={`${medianPct}%`}
        gradient={KPI_CARD_STYLES.teal}
      />
      <KpiCard
        label="Active Attorneys"
        value={`${activeCount}`}
        gradient={KPI_CARD_STYLES.dark}
      />
      <KpiCard
        label="Hours Captured"
        value={totals.hourglass_hours.toFixed(1)}
        suffix="hrs"
        gradient={KPI_CARD_STYLES.blue}
      />
    </div>
  );
}
